import { useState } from "react";

import { useAccounts } from "../../api/hooks/useAccounts";
import {
  COMMISSION_MODELS,
  FILL_TIME_ROUNDING,
  PNL_METHODS,
  type CommissionModel,
  type FillTimeRounding,
  type PnlMethod,
  type UserSettings,
} from "../../lib/settings";

const COMMISSION_LABELS: Record<CommissionModel, string> = {
  flat_fee: "Flat fee per trade",
  per_share: "Per share / contract",
  percentage: "Percentage of notional",
};

const FILL_TIME_LABELS: Record<FillTimeRounding, string> = {
  none: "No rounding",
  nearest_minute: "Nearest minute",
  nearest_5min: "Nearest 5 minutes",
};

const PNL_LABELS: Record<PnlMethod, string> = {
  fifo: "FIFO (first-in, first-out)",
  lifo: "LIFO (last-in, first-out)",
  average_cost: "Average cost",
};

export type TradingDefaultsSectionProps = {
  settings: UserSettings;
  onChange: (next: UserSettings) => void;
};

export function TradingDefaultsSection({
  settings,
  onChange,
}: TradingDefaultsSectionProps) {
  const { data, isLoading } = useAccounts();
  const accounts = (data?.items ?? []).filter((a) => !a.is_deleted);
  const [saved, setSaved] = useState(false);

  const update = (patch: Partial<UserSettings["trading"]>) => {
    onChange({ ...settings, trading: { ...settings.trading, ...patch } });
    setSaved(true);
    window.setTimeout(() => {
      setSaved(false);
    }, 1500);
  };

  return (
    <section aria-labelledby="trading-defaults-heading" className="space-y-6">
      <header>
        <h2
          id="trading-defaults-heading"
          className="font-display text-xl font-medium tracking-tight text-on-surface"
        >
          Trading defaults
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          Defaults applied when you record a new trade. Some preferences
          will plumb through to the analytics engine as features land.
        </p>
      </header>

      <div className="space-y-6">
        <Field id="trading-default-account" label="Default account">
          {isLoading ? (
            <p className="text-sm text-on-surface-muted">Loading accounts…</p>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-on-surface-muted">
              You don&apos;t have any accounts yet — create one on the
              Accounts page first.
            </p>
          ) : (
            <select
              id="trading-default-account"
              value={settings.trading.defaultAccountId ?? ""}
              onChange={(event) => {
                update({
                  defaultAccountId:
                    event.target.value === "" ? null : event.target.value,
                });
              }}
              className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring"
            >
              <option value="">— No default —</option>
              {accounts.map((account) => (
                <option key={account.account_id} value={account.account_id}>
                  {account.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <RadioField
          legend="Commission model"
          name="commissionModel"
          options={COMMISSION_MODELS}
          labels={COMMISSION_LABELS}
          value={settings.trading.commissionModel}
          onChange={(value) => {
            update({ commissionModel: value });
          }}
        />

        <RadioField
          legend="Fill-time rounding"
          name="fillTimeRounding"
          options={FILL_TIME_ROUNDING}
          labels={FILL_TIME_LABELS}
          value={settings.trading.fillTimeRounding}
          onChange={(value) => {
            update({ fillTimeRounding: value });
          }}
        />

        <RadioField
          legend="P&L calculation method"
          name="pnlMethod"
          options={PNL_METHODS}
          labels={PNL_LABELS}
          value={settings.trading.pnlMethod}
          onChange={(value) => {
            update({ pnlMethod: value });
          }}
        />

        <div aria-live="polite" className="h-5 text-xs text-on-surface-muted">
          {saved ? "Saved." : ""}
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
      <label
        htmlFor={id}
        className="pt-1.5 text-sm font-medium text-on-surface"
      >
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

type RadioFieldProps<T extends string> = {
  legend: string;
  name: string;
  options: ReadonlyArray<T>;
  labels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
};

function RadioField<T extends string>({
  legend,
  name,
  options,
  labels,
  value,
  onChange,
}: RadioFieldProps<T>) {
  return (
    <fieldset className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
      <legend className="float-left pt-1.5 text-sm font-medium text-on-surface sm:float-none">
        {legend}
      </legend>
      <div className="min-w-0 space-y-1.5">
        {options.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2 text-sm text-on-surface"
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={() => {
                onChange(option);
              }}
              className="h-4 w-4"
            />
            <span>{labels[option]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
